/*
 * cart-recommendations-slider.js — CartIntel 2026-09-09 (CDIONIS-93).
 * Preserves the supplied v2's server-rendered card markup, collection-first
 * normal mode, source-aware smart backfill, abort/debounce, BV and QuickAdd.
 * The original v2 is retained in originals/assets for audit and rollback.
 *
 * Eight candidates per source; at most four display cards. Liquid owns state
 * and product exclusions. Only the recommendation mount is replaced; never
 * the cart form/body/summary. No cart mutations are performed by this asset.
 */
(function () {
  'use strict';

  /* Shopify's t filter HTML-escapes locale strings, and the config script
     carries them escaped ("You&#39;re", "&amp;"). textContent does not decode
     entities, so decode once here. A detached textarea never executes markup. */
  const DECODE_BOX = document.createElement('textarea');
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    DECODE_BOX.innerHTML = value;
    return DECODE_BOX.value;
  }
  if (window.DionisCartRecs) return;
  const CANDIDATE_LIMIT = 8;
  const DISPLAY_LIMIT = 4;
  const SECTION_ID = 'cart-recommendations';
  const ROOT = '.cart-recommendations';
  const CARD = '.product-card--root';
  const STATE = '[data-cart-intelligence-state]';
  const FALLBACK_HANDLE = 'creams-moisturizers';
  const DEBOUNCE_MS = 150;
  const REQUEST_BUDGET_MS = 15000;
  const warned = new Set();
  const rendered = new WeakMap();
  let controller = null;
  let timer = 0;
  let revision = 0;
  let lastCart = null;
  let lastError = null;

  function config() { return (window.cartRecsConfig && window.cartRecsConfig.drawer) || {}; }
  function warnOnce(key, message) {
    if (warned.has(key)) return;
    warned.add(key);
    console.warn('[CartRecs]', message);
  }
  function route(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }
  function planId(item) {
    return Number(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || 0;
  }
  function itemSignature(cart) {
    return cart.items.map(item => `${item.key}:${item.quantity}:${planId(item)};`).join('');
  }
  function cartFingerprint(cart) {
    return JSON.stringify({
      items: cart.items.map(item => [item.key, item.quantity, planId(item), item.final_price, item.final_line_price,
        item.line_level_discount_allocations || item.line_level_discount_applications || [], item.discounts || []]),
      total: cart.total_price, subtotal: cart.items_subtotal_price, discount: cart.total_discount,
      currency: cart.currency, discounts: cart.cart_level_discount_applications || [], codes: cart.discount_codes || []
    });
  }
  function noDiscounts(cart) {
    if (Number(cart.total_discount) !== 0) return false;
    if ((cart.cart_level_discount_applications || []).length || (cart.discount_codes || []).length) return false;
    if (Number(cart.items_subtotal_price) > Number(cart.total_price)) return false;
    return cart.items.every(item => !((item.line_level_discount_allocations || []).length ||
      (item.line_level_discount_applications || []).length || (item.discounts || []).length ||
      Number(item.total_discount) > 0 || Number(item.final_price) < Number(item.original_price)));
  }
  function containers() { return Array.from(document.querySelectorAll('[id="cart-recommendations"]')); }
  function parse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector(ROOT);
    if (!root) throw new Error('The recommendation section has no expected root.');
    const result = { root, kind: root.dataset.sourceKind || 'unknown', handle: root.dataset.collectionHandle || '', cards: Array.from(root.querySelectorAll(CARD)) };
    for (const card of result.cards) {
      card.dataset.cartRecsOrigin = result.kind === 'collection' ? result.handle : result.kind;
    }
    return result;
  }
  async function get(url, signal, asJSON = false) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal,
      headers: { Accept: asJSON ? 'application/json' : 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return asJSON ? response.json() : response.text();
  }
  async function readCart(signal) {
    const cart = await get(route('cart.js'), signal, true);
    if (!cart || !Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) {
      throw new Error('Invalid cart response.');
    }
    return cart;
  }
  function readState(node) {
    if (!node) return null;
    const d = node.dataset;
    return { mode: d.recsMode || 'algorithmic', gap: Number(d.shippingGap) || 0,
      gapMoney: d.shippingGapMoney || '', subShipping: d.subShipping === 'true',
      shippableTotal: Number(d.shippableTotal) || 0, signature: d.itemSignature,
      total: Number(d.cartTotal), discount: Number(d.cartDiscount), count: Number(d.cartItemCount) };
  }
  function matches(s, cart) {
    return !!s && s.signature === itemSignature(cart) && s.total === cart.total_price &&
      s.discount === Number(cart.total_discount || 0) && s.count === cart.item_count;
  }
  async function statesFor(mounts, cart, signal) {
    const byRoot = new Map();
    const stale = [];
    for (const mount of mounts) {
      const root = mount.closest('cart-element');
      if (!root || byRoot.has(root)) continue;
      const s = readState(root.querySelector(STATE));
      byRoot.set(root, s);
      if (!matches(s, cart)) stale.push(root);
    }
    if (!stale.length) return byRoot;
    // A discount-only event can leave the original carrier stale. Request fresh
    // Liquid state, not a second JS implementation of the shipping calculations.
    const sectionNames = Array.from(new Set(stale.map(root => `cart-${root.dataset.view}`)));
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionNames.join(','));
    try {
      const result = await get(url.pathname + url.search, signal, true);
      for (const root of stale) {
        const html = result[`cart-${root.dataset.view}`];
        if (typeof html !== 'string' || !html.trim()) { byRoot.set(root, null); continue; }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`cart-element[data-view="${root.dataset.view}"] ${STATE}`) || doc.querySelector(STATE);
        const s = readState(fresh);
        if (!matches(s, cart)) { byRoot.set(root, null); continue; }
        byRoot.set(root, s);
        // Replace metadata only. Never replace the live cart or execute scripts.
        const old = root.querySelector(STATE);
        if (old && fresh) old.replaceWith(fresh.cloneNode(true));
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      for (const root of stale) byRoot.set(root, null);
      warnOnce('state-unavailable', 'Fresh Liquid state is unavailable; using normal recommendations without gap claims.');
    }
    return byRoot;
  }
  function unique(cards, max = CANDIDATE_LIMIT, excluded = new Set()) {
    const seen = new Set(excluded);
    const out = [];
    for (const card of cards) {
      const id = card.dataset.productId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(card);
      if (out.length >= max) break;
    }
    return out;
  }
  function available(card) { return card.dataset.available === 'true'; }
  function rankGap(cards, gap) {
    const valid = unique(cards).filter(card => available(card) && Number.isFinite(Number(card.dataset.priceMin)) && Number(card.dataset.priceMin) > 0);
    const pass1 = valid.filter(card => Number(card.dataset.priceMin) >= gap).sort((a, b) => Number(a.dataset.priceMin) - Number(b.dataset.priceMin));
    const pass2 = valid.filter(card => Number(card.dataset.priceMin) < gap).sort((a, b) => Number(b.dataset.priceMin) - Number(a.dataset.priceMin));
    return { cards: pass1.concat(pass2), pass1 };
  }
  function makeFetcher(signal) {
    const memo = new Map();
    return function fetchOnce(kind, value) {
      const key = `${kind}:${value}`;
      if (!memo.has(key)) {
        const url = kind === 'collection' ? route(`collections/${encodeURIComponent(value)}?section_id=${SECTION_ID}`) :
          route(`recommendations/products?section_id=${SECTION_ID}&product_id=${encodeURIComponent(value)}&limit=${CANDIDATE_LIMIT}&intent=related`);
        memo.set(key, get(url, signal).then(parse));
      }
      return memo.get(key);
    };
  }
  async function safeSource(fetchOnce, kind, value, signal) {
    try { return await fetchOnce(kind, value); }
    catch (error) {
      if (signal.aborted || error.name === 'AbortError') throw error;
      warnOnce(`${kind}:${value}`, `A ${kind} recommendation source failed; trying the remaining sources.`);
      return null;
    }
  }
  async function smart(cart, fetchOnce, signal) {
    const ids = Array.from(new Set(cart.items.map(item => item.product_id).filter(Boolean)));
    const selected = ids.slice(0, config().useMultiProductRecs === false ? 1 : 2);
    const results = await Promise.all(selected.map(id => safeSource(fetchOnce, 'recommendations', id, signal)));
    const parsed = results.filter(Boolean);
    let base = null;
    for (const kind of ['recommendations', 'collection', 'fallback']) {
      base = parsed.find(p => p.kind === kind && p.cards.length) || null;
      if (base) break;
    }
    if (base) {
      // Real recommendations cannot be displaced by an unrelated fallback.
      const extras = parsed.filter(p => p !== base && (base.kind !== 'recommendations' || p.kind === 'recommendations'));
      return { base, cards: unique([ ...base.cards, ...extras.flatMap(p => p.cards) ]) };
    }
    const fallback = await safeSource(fetchOnce, 'collection', FALLBACK_HANDLE, signal);
    if (fallback) return { base: fallback, cards: unique(fallback.cards) };
    const empty = parsed.find(p => p.root);
    if (empty) return { base: empty, cards: [] };
    throw new Error('All recommendation sources failed.');
  }
  async function normal(cart, fetchOnce, signal) {
    const cfg = config();
    if (!cart.items.length) {
      const result = await fetchOnce('collection', FALLBACK_HANDLE);
      return { base: result, cards: unique(result.cards), mode: 'algorithmic', heading: null };
    }
    if (cfg.collectionHandle) {
      const result = await safeSource(fetchOnce, 'collection', cfg.collectionHandle, signal);
      // A collection URL can resolve to the section's fallback. Do not mistake
      // those cards for the merchant's curated collection.
      if (result && result.kind === 'collection' && result.handle === cfg.collectionHandle) {
        let cards = unique(result.cards);
        if (cards.length < DISPLAY_LIMIT) {
          try { cards = unique(cards.concat((await smart(cart, fetchOnce, signal)).cards)); }
          catch (error) { if (signal.aborted || !cards.length) throw error; }
        }
        return { base: result, cards, mode: 'algorithmic', heading: null };
      }
    }
    return { ...(await smart(cart, fetchOnce, signal)), mode: 'algorithmic', heading: null };
  }
  async function compose(cart, s, fetchOnce, signal) {
    const cfg = config();
    let mode = s && cfg.intelligenceEnabled === true ? s.mode : 'algorithmic';
    if (!cart.items.length) mode = 'algorithmic';
    const cfgHeadings = cfg.headings || {};
    const away = () => (cfgHeadings.gapAway || '').split('[$]').join(s ? s.gapMoney : '');
    if (!['baby', 'gap', 'baby-gap'].includes(mode)) return normal(cart, fetchOnce, signal);
    const needsWomen = mode === 'baby' || mode === 'baby-gap';
    const needsGap = mode === 'gap' || mode === 'baby-gap';
    if ((needsWomen && !cfg.womensCollectionHandle) || (needsGap && !cfg.gapCollectionHandle)) {
      warnOnce(`unselected:${mode}`, `The ${mode} collection setting is unselected; using normal recommendations.`);
      return normal(cart, fetchOnce, signal);
    }
    const [women, gap] = await Promise.all([
      needsWomen ? safeSource(fetchOnce, 'collection', cfg.womensCollectionHandle, signal) : null,
      needsGap ? safeSource(fetchOnce, 'collection', cfg.gapCollectionHandle, signal) : null
    ]);
    const womenValid = women && women.kind === 'collection' && women.handle === cfg.womensCollectionHandle;
    const gapValid = gap && gap.kind === 'collection' && gap.handle === cfg.gapCollectionHandle;
    if ((needsWomen && !womenValid) || (mode === 'gap' && !gapValid)) return normal(cart, fetchOnce, signal);
    let womenCards = womenValid ? unique(women.cards).filter(available) : [];
    const ranked = gapValid ? rankGap(gap.cards, s.gap) : { cards: [], pass1: [] };
    let base = needsWomen ? women : gap;
    let cards;
    let heading;
    if (mode === 'gap') {
      cards = ranked.cards;
      if (cards.length < DISPLAY_LIMIT) {
        try { cards = unique(cards.concat((await smart(cart, fetchOnce, signal)).cards.filter(available))); }
        catch (error) { if (signal.aborted || !cards.length) throw error; }
      }
      const displayed = cards.slice(0, DISPLAY_LIMIT);
      const pass1Ids = new Set(ranked.pass1.map(card => card.dataset.productId));
      const canUnlock = displayed.length === DISPLAY_LIMIT && s.gap > 0 && noDiscounts(cart) && displayed.every(card =>
        pass1Ids.has(card.dataset.productId) && available(card) && card.dataset.requiresPlan !== 'true' &&
        !card.querySelector('.sale-now, .sale-now-cart-recs'));
      heading = canUnlock && cfgHeadings.gapUnlock ? cfgHeadings.gapUnlock : away();
    } else {
      const closer = mode === 'baby-gap' && cfg.slot4Rule !== 'women-only' ? ranked.pass1[0] : null;
      const excluded = new Set(closer ? [closer.dataset.productId] : []);
      womenCards = unique(womenCards, CANDIDATE_LIMIT, excluded);
      const target = closer ? DISPLAY_LIMIT - 1 : DISPLAY_LIMIT;
      cards = womenCards;
      if (cards.length < target) {
        try { cards = unique(cards.concat((await smart(cart, fetchOnce, signal)).cards.filter(available)), CANDIDATE_LIMIT, excluded); }
        catch (error) { if (signal.aborted || (!cards.length && !closer)) throw error; }
      }
      cards = cards.slice(0, target);
      if (closer) cards.push(closer);
      heading = closer ? away() : cfgHeadings.baby;
    }
    if (!cards.length) return normal(cart, fetchOnce, signal);
    return { base, cards, mode, heading };
  }
  function render(mount, result) {
    const root = result.base.root.cloneNode(true);
    const items = root.querySelector('.cart--recommendations--items');
    if (!items) {
      if (result.cards.length) throw new Error('Recommendation items container missing.');
    } else if (result.cards.length) {
      items.replaceChildren(...result.cards.slice(0, DISPLAY_LIMIT).map(card => card.cloneNode(true)));
    } else {
      items.querySelectorAll(CARD).forEach(card => card.remove());
    }
    const heading = root.querySelector('.cart-recs--heading');
    if (heading && result.heading) heading.textContent = decodeEntities(result.heading);
    // The original v2 shape: heading + items, no extra wrapper around cards.
    mount.replaceChildren(...Array.from(root.childNodes));
    mount.dataset.cartRecsRenderedMode = result.mode;
    mount.setAttribute('aria-busy', 'false');
  }
  function integrations(mounts) {
    if (window.ProductQuickAdd && typeof window.ProductQuickAdd.init === 'function') window.ProductQuickAdd.init();
    if (window.$BV && typeof window.$BV.ui === 'function') {
      const ids = new Set();
      mounts.forEach(mount => mount.querySelectorAll(CARD).forEach(card => { if (card.dataset.productId) ids.add(card.dataset.productId); }));
      if (ids.size) {
        try { window.$BV.ui('rr', 'inline_ratings', { productIds: Array.from(ids) }); }
        catch (error) { warnOnce('bv', 'BazaarVoice could not reinitialize inline ratings.'); }
      }
    }
  }
  function updateEmptyDisplay(cart) {
    // Preserve cart-page empty-state handling. Scope the former global selector
    // operations to every drawer instance and use the cart snapshot, not stale
    // .cart--item DOM left behind by final-item removal in Blockshop.
    document.querySelectorAll('cart-element[data-view="drawer"]').forEach(root => {
      const empty = cart.item_count === 0;
      root.dataset.hasItems = String(!empty);
      for (const [selector, show] of [['.cart--no-items', empty], ['.cart--continue-shopping-top', !empty],
        ['.cart--continue-shopping-bottom', !empty], ['.cart--notes', !empty]]) {
        const el = root.querySelector(selector);
        if (el) el.style.setProperty('display', show ? 'block' : 'none', 'important');
      }
      const shipping = root.querySelector('.cart--shipping');
      if (shipping) {
        if (empty || !cart.items.some(item => item.requires_shipping)) shipping.style.setProperty('display', 'none', 'important');
        else shipping.style.removeProperty('display');
      }
    });
  }
  async function refresh(force = false) {
    if (timer) { clearTimeout(timer); timer = 0; }
    if (controller) controller.abort();
    const current = new AbortController();
    controller = current;
    const signal = current.signal;
    const version = revision;
    const deadline = setTimeout(() => current.abort(), REQUEST_BUDGET_MS);
    const mounts = containers();
    try {
      if (!mounts.length) return;
      const cart = await readCart(signal);
      if (signal.aborted || version !== revision) return;
      updateEmptyDisplay(cart);
      const states = await statesFor(mounts, cart, signal);
      if (signal.aborted || version !== revision) return;
      const fingerprint = cartFingerprint(cart);
      const fetchOnce = makeFetcher(signal);
      const compositions = new Map();
      const updated = [];
      for (const mount of mounts) {
        if (!mount.isConnected) continue;
        const s = states.get(mount.closest('cart-element')) || null;
        const signature = fingerprint + JSON.stringify(s) + JSON.stringify(config());
        if (!force && rendered.get(mount) === signature && mount.childNodes.length) continue;
        mount.setAttribute('aria-busy', 'true');
        const key = JSON.stringify(s);
        if (!compositions.has(key)) compositions.set(key, compose(cart, s, fetchOnce, signal));
        const result = await compositions.get(key);
        if (signal.aborted || version !== revision) return;
        render(mount, result);
        rendered.set(mount, signature);
        updated.push(mount);
      }
      if (updated.length) integrations(updated);
      lastCart = { itemCount: cart.item_count, total: cart.total_price, fingerprint };
      lastError = null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        lastError = error.message;
        console.error('[CartRecs] Refresh failed; existing cards retained:', error);
      }
    } finally {
      clearTimeout(deadline);
      if (controller === current) {
        controller = null;
        mounts.forEach(mount => mount.setAttribute('aria-busy', 'false'));
      }
    }
  }
  function schedule() {
    revision += 1;
    if (controller) controller.abort();
    clearTimeout(timer);
    timer = setTimeout(() => { timer = 0; refresh(); }, DEBOUNCE_MS);
  }
  ['theme:cart:updated', 'theme:cart:add', 'theme:cart:quantity'].forEach(name => window.addEventListener(name, schedule));
  window.addEventListener('theme:drawer:open', event => {
    if (!event.detail || event.detail.view === 'cart-drawer') schedule();
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') schedule(); });
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
  window.DionisCartRecs = Object.freeze({
    refresh: () => refresh(true),
    diagnostics: () => ({ candidateLimit: CANDIDATE_LIMIT, displayLimit: DISPLAY_LIMIT,
      inFlight: !!controller, lastCart, lastError, mounts: containers().length })
  });
})();